import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import {
  financeLegacyService,
  FinanceLegacyEntry,
  FinanceLegacyOptions,
  FinanceLegacyUser,
  ENTRY_TYPE_EXPENSE,
  ENTRY_TYPE_INCOME,
} from '@services/finance-legacy.service.ts';
import { toastService } from '@services/toast.service.ts';

// Вікно створення/редагування запису (2026-08-21) - перше конкретне write-
// вікно на шляху поступового перенесення ведення "Старих фінансів" у наш
// вебдодаток. Редагування (entry заданий) доступне лише для власних
// (sourceApp=WEB) записів - бек кине 403 на легасі-записах.
// Юзер легасі-бази обирається явно, без автомапінгу на залогіненого адміна.
export const FinanceLegacyEntryFormDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  type: number;
  options: FinanceLegacyOptions;
  entry?: FinanceLegacyEntry;
  onSaved: () => void;
}> = ({ open, onClose, type, options, entry, onSaved }) => {
  const [users, setUsers] = useState<FinanceLegacyUser[]>([]);
  const [projectId, setProjectId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [amount, setAmount] = useState('');
  const [empId, setEmpId] = useState('');
  const [comment, setComment] = useState('');
  const [createdUserId, setCreatedUserId] = useState('');
  const [createdAt, setCreatedAt] = useState<Date | null>(new Date());
  const [saving, setSaving] = useState(false);

  const isEdit = !!entry;

  // entry_sources.type корелює 1:1 з entries.type на всіх наявних даних
  // (перевірено SQL-запитом, 2026-08-21) - список джерел у формі фільтрується
  // за напрямком, бек так само валідує (validateEntryRefs()).
  const availableSources = options.sources.filter((s) => s.type === type);

  useEffect(() => {
    if (!open) return;
    financeLegacyService.getUsers().then(setUsers).catch(() => toastService.error('Не вдалося завантажити юзерів.'));
  }, [open]);

  // FIX (код-рев'ю, 2026-08-21): defaultsAppliedRef гарантує, що дефолти
  // "Софт"/"Дима"/"UAH" застосовуються РІВНО ОДИН раз за відкриття діалогу.
  // До фіксу цей ефект залежав від `options` напряму - якщо getOptions() на
  // сторінці резолвився ПІСЛЯ відкриття діалогу (юзер встиг натиснути
  // "Додати" і почати вводити суму/коментар до завантаження options),
  // ефект перезапускався і мовчки стирав уже введені користувачем дані.
  const defaultsAppliedRef = useRef(false);

  // Ефект A - скидання/заповнення полів на самому відкритті (редагування -
  // з entry, створення - в пусте). Залежить лише від open/entry, НЕ від
  // options - тому більше не перетирає ввід користувача при пізньому
  // завантаженні довідників.
  useEffect(() => {
    if (!open) {
      defaultsAppliedRef.current = false;
      return;
    }

    if (entry) {
      setProjectId(entry.projectId ? String(entry.projectId) : '');
      setSourceId(String(entry.sourceId));
      setCurrencyId(String(entry.currencyId));
      setAmount(entry.amount != null ? String(entry.amount) : '');
      setEmpId(entry.empId ? String(entry.empId) : '');
      setComment(entry.comment ?? '');
      setCreatedUserId(entry.createdUserId ? String(entry.createdUserId) : '');
      setCreatedAt(new Date(entry.createdAt));
      return;
    }

    setProjectId('');
    setSourceId('');
    setCurrencyId('');
    setAmount('');
    setEmpId('');
    setComment('');
    setCreatedUserId('');
    setCreatedAt(new Date());

    financeLegacyService.getMyLegacyUserMapping()
      .then(({ legacyUserId }) => { if (legacyUserId) setCreatedUserId(String(legacyUserId)); })
      .catch(() => {});
  }, [open, entry]);

  // Ефект B - дефолти "Софт"/"Дима"/"UAH" (за рішенням користувача), лише
  // при СТВОРЕННІ, лише коли options реально завантажені (не порожній
  // emptyOptions-заглушка), і лише ОДИН раз за відкриття (defaultsAppliedRef).
  // "Дима" (source.type=EXPENSE) - дефолт лише для "Витрати", для "Доходи"
  // джерело лишається пустим (немає еквівалентного дефолту для доходів).
  useEffect(() => {
    if (!open || entry || defaultsAppliedRef.current) return;
    if (options.projects.length === 0) return;

    setProjectId(String(options.projects.find((p) => p.label === 'Софт')?.value ?? ''));
    setCurrencyId(String(options.currencies.find((c) => c.label === 'UAH')?.value ?? ''));
    if (type === ENTRY_TYPE_EXPENSE) {
      setSourceId(String(options.sources.find((s) => s.label === 'Дима' && s.type === type)?.value ?? ''));
    }

    defaultsAppliedRef.current = true;
  }, [open, entry, options, type]);

  const valid = sourceId && currencyId && amount && Number(amount) > 0 && createdUserId;

  const handleSave = async () => {
    if (!valid) {
      toastService.error('Заповніть обовʼязкові поля: джерело, валюта, сума, юзер.');
      return;
    }
    setSaving(true);
    try {
      const sourceIdNum = Number(sourceId);
      const currencyIdNum = Number(currencyId);
      const amountNum = Number(amount);
      const createdUserIdNum = Number(createdUserId);
      const createdAtIso = createdAt ? createdAt.toISOString() : undefined;

      if (isEdit) {
        // FIX (код-рев'ю, 2026-08-21): explicit null для очищуваних полів -
        // undefined на PATCH означає "не чіпати", не "очистити".
        await financeLegacyService.updateEntry(entry!.id, {
          projectId: projectId ? Number(projectId) : null,
          sourceId: sourceIdNum,
          currencyId: currencyIdNum,
          amount: amountNum,
          empId: empId ? Number(empId) : null,
          comment: comment || null,
          createdUserId: createdUserIdNum,
          createdAt: createdAtIso,
        });
        toastService.success('Запис оновлено.');
      } else {
        await financeLegacyService.createEntry({
          type,
          projectId: projectId ? Number(projectId) : undefined,
          sourceId: sourceIdNum,
          currencyId: currencyIdNum,
          amount: amountNum,
          empId: empId ? Number(empId) : undefined,
          comment: comment || undefined,
          createdUserId: createdUserIdNum,
          createdAt: createdAtIso,
        });
        toastService.success('Запис створено.');
      }
      onSaved();
      onClose();
    } catch {
      toastService.error(isEdit ? 'Не вдалося оновити запис.' : 'Не вдалося створити запис.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редагувати запис' : 'Додати запис'}
      actions={<Button variant="contained" onClick={handleSave} disabled={saving || !valid}>{isEdit ? 'Зберегти' : 'Створити'}</Button>}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField select label="Проєкт" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <MenuItem value="">—</MenuItem>
          {options.projects.map((p) => <MenuItem key={p.value} value={String(p.value)}>{p.label}</MenuItem>)}
        </TextField>

        <TextField select required label="Джерело" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
          {availableSources.map((s) => <MenuItem key={s.value} value={String(s.value)}>{s.label}</MenuItem>)}
        </TextField>

        <TextField select required label="Валюта" value={currencyId} onChange={(e) => setCurrencyId(e.target.value)}>
          {options.currencies.map((c) => <MenuItem key={c.value} value={String(c.value)}>{c.label}</MenuItem>)}
        </TextField>

        <TextField required type="number" label="Сума" value={amount} onChange={(e) => setAmount(e.target.value)} />

        {type !== ENTRY_TYPE_INCOME && (
          <TextField select label="Співробітник" value={empId} onChange={(e) => setEmpId(e.target.value)}>
            <MenuItem value="">—</MenuItem>
            {options.employees.map((emp) => <MenuItem key={emp.value} value={String(emp.value)}>{emp.label}</MenuItem>)}
          </TextField>
        )}

        <TextField
          select
          required
          label="Юзер (від імені якого пишеться запис)"
          value={createdUserId}
          onChange={(e) => setCreatedUserId(e.target.value)}
        >
          {users.map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>)}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
          <DatePicker label="Дата" value={createdAt} onChange={setCreatedAt} />
        </LocalizationProvider>

        <TextField multiline minRows={2} label="Коментар" value={comment} onChange={(e) => setComment(e.target.value)} />
      </Box>
    </BaseDialog>
  );
};
